/**
 * canonical URL を組み立てる。
 *
 * 詳細ページの canonical をテンプレート文字列で書くと、データが取れなかった
 * ときに `${slug ?? ""}` が空になって `https://yumesaku.tech/learn/tracks/`
 * のような**親の URL** が出る。親は実在する別ページなので、
 * 「この詳細ページは一覧ページの複製だ」と検索エンジンに伝えてしまう。
 *
 * canonical は無くても困らない (その URL 自身が正として扱われる) が、
 * 間違ったものが有ると害になる。なので**取れないときは出さない**。
 */

const DEFAULT_BASE_URL = "https://yumesaku.tech";

/** canonical と sitemap にはハッシュもクエリも載せない */
function stripFragmentAndQuery(segment: string): string {
  return segment.split("#")[0].split("?")[0];
}

/**
 * 引数の1つでも欠けていたら `undefined` を返す。
 *
 * ```ts
 * canonicalUrl("learn/tracks", data?.track.slug)
 * //  slug あり → "https://yumesaku.tech/learn/tracks/frontend"
 * //  slug なし → undefined  (親 URL に落とさない)
 * ```
 */
/** id や章番号がそのまま渡ってくるので number も受ける */
type Segment = string | number | null | undefined;

export function canonicalUrl(...segments: Segment[]): string | undefined {
  const base = DEFAULT_BASE_URL;
  if (segments.length === 0) return base;

  const parts: string[] = [];
  for (const segment of segments) {
    if (segment === null || segment === undefined) return undefined;
    const cleaned = stripFragmentAndQuery(String(segment)).trim();
    // 区切りだけの引数 ("/" や "") は、欠けたデータと見分けが付かない
    const pieces = cleaned.split("/").filter((piece) => piece.length > 0);
    if (pieces.length === 0) return undefined;
    parts.push(...pieces);
  }

  return `${base}/${parts.join("/")}`;
}
